import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import Konva from 'konva';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { InputFieldComponent } from '../inputs/input-field/input-field.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { ModalComponent } from '../modal/modal.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import {
  ANNOTATION_COLORS,
  ANNOTATION_SIZE_FACTORS,
  AnnotationShape,
  AnnotationTool,
  buildAnnotationNode,
  getBaseStrokeWidth,
  isAnnotationShapeMeaningful,
  parseImageAnnotation,
  SegmentAnnotationShape,
  serializeImageAnnotation,
  translateAnnotationShape,
} from './image-annotation.model';

export interface ImageAnnotatorResult {
  /** Beschriftetes Bild in Originalgrösse (JPEG). */
  blob: Blob;
  /** Zeichnung als Vektoren, siehe `ImageAnnotation`. */
  annotationJson: string;
}

interface TextEditState {
  /** `null` = neuer Text an `x`/`y`; sonst Index der Form, deren Text oder Mass bearbeitet wird. */
  index: number | null;
  mode: 'text' | 'label';
  x: number;
  y: number;
}

const TOOL_OPTIONS: { tool: AnnotationTool; iconName: string; label: string }[] = [
  { tool: 'select', iconName: 'arrow_selector_tool', label: 'BILD_BESCHRIFTEN_AUSWAEHLEN' },
  { tool: 'freehand', iconName: 'draw', label: 'BILD_BESCHRIFTEN_FREIHAND' },
  { tool: 'line', iconName: 'horizontal_rule', label: 'BILD_BESCHRIFTEN_LINIE' },
  { tool: 'arrow', iconName: 'north_east', label: 'BILD_BESCHRIFTEN_PFEIL' },
  { tool: 'dimension', iconName: 'straighten', label: 'BILD_BESCHRIFTEN_VERMASSUNG' },
  { tool: 'rect', iconName: 'crop_square', label: 'BILD_BESCHRIFTEN_RECHTECK' },
  { tool: 'ellipse', iconName: 'circle', label: 'BILD_BESCHRIFTEN_ELLIPSE' },
  { tool: 'text', iconName: 'title', label: 'BILD_BESCHRIFTEN_TEXT' },
];

const SELECTION_COLOR = '#2f80ed';
const MAX_ZOOM_FACTOR = 8;
/** Mindestbreite der Trefferfläche auf dem Bildschirm, damit auch dünne Linien mit dem Finger greifbar sind. */
const MIN_HIT_WIDTH_ON_SCREEN = 32;
/** Griffe an den Enden einer gewählten Linie/Vermassung: sichtbarer Radius und Trefferfläche, beides in Bildschirm-Pixeln. */
const ENDPOINT_HANDLE_RADIUS_ON_SCREEN = 10;
const ENDPOINT_HANDLE_HIT_RADIUS_ON_SCREEN = 24;

/**
 * Vollbild-Editor zum Beschriften eines Fotos: Freihand, Linien, Pfeile, Vermassungen, Rechtecke, Kreise und Text.
 *
 * Bewusst ohne API-Zugriff: der Editor liefert das gerenderte Bild in Originalgrösse plus die Zeichnung als Vektoren;
 * wohin das gespeichert wird, entscheidet der Aufrufer (Lightbox → `POST /v2/documents/:id/annotation`).
 * Alle Koordinaten sind Pixel des Originalbilds, damit Anzeige-Zoom und Export dieselben Daten verwenden.
 */
@Component({
  selector: 'work-ui-image-annotator',
  imports: [
    ButtonComponent,
    IconComponent,
    InputFieldComponent,
    LoadingSpinnerComponent,
    ModalComponent,
    ReactiveFormsModule,
    TooltipDirective,
    TranslateModule,
  ],
  templateUrl: './image-annotator.component.html',
  styleUrl: './image-annotator.component.scss',
  host: {
    '(window:keydown)': 'handleKeydown($event)',
    '(window:resize)': 'handleResize()',
  },
})
export class ImageAnnotatorComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);

  /** Bild-URL des unveränderten Originals. Der Server muss CORS erlauben, sonst lässt sich der Canvas nicht exportieren. */
  public readonly imageSrc = input.required<string>();
  /** Bereits gespeicherte Zeichnung, wird zum Weiterbearbeiten geladen. */
  public readonly annotationJson = input<string | null | undefined>(null);
  public readonly saving = input<boolean>(false);

  public readonly saved = output<ImageAnnotatorResult>();
  public readonly cancelled = output<void>();

  private readonly stageContainer = viewChild.required<ElementRef<HTMLDivElement>>('stageContainer');
  private readonly textInputField = viewChild<InputFieldComponent>('textInputField');
  private readonly unsavedChangesModal = viewChild.required<ModalComponent>('unsavedChangesModal');

  protected readonly tool = signal<AnnotationTool>('select');
  protected readonly color = signal<string>(ANNOTATION_COLORS[0]);
  protected readonly sizeFactor = signal<number>(ANNOTATION_SIZE_FACTORS[1]);
  protected readonly shapes = signal<AnnotationShape[]>([]);
  protected readonly selectedIndex = signal<number | null>(null);
  protected readonly isImageLoading = signal<boolean>(true);
  protected readonly imageLoadFailed = signal<boolean>(false);
  protected readonly isExporting = signal<boolean>(false);
  protected readonly textEdit = signal<TextEditState | null>(null);
  private readonly undoStack = signal<AnnotationShape[][]>([]);
  private readonly redoStack = signal<AnnotationShape[][]>([]);
  /** Stand beim Öffnen. Undo stellt dieselbe Array-Referenz wieder her — wer alles zurücknimmt, hat nichts geändert. */
  private readonly initialShapes = signal<AnnotationShape[]>([]);

  protected readonly canUndo = computed(() => this.undoStack().length > 0);
  protected readonly canRedo = computed(() => this.redoStack().length > 0);
  protected readonly hasUnsavedChanges = computed(() => this.shapes() !== this.initialShapes());
  protected readonly isBusy = computed(() => this.saving() || this.isExporting());
  protected readonly saveLoadingState = computed(() => (this.isBusy() ? 'loading' : 'none'));
  protected readonly textEditPlaceholder = computed(() =>
    this.textEdit()?.mode === 'label' ? 'BILD_BESCHRIFTEN_MASS_EINGEBEN' : 'BILD_BESCHRIFTEN_TEXT_EINGEBEN',
  );

  private readonly renderEffect = effect(() => {
    const shapes = this.shapes();
    const selectedIndex = this.selectedIndex();
    const tool = this.tool();
    untracked(() => this.renderShapes(shapes, selectedIndex, tool));
  });

  protected readonly TOOL_OPTIONS = TOOL_OPTIONS;
  protected readonly ANNOTATION_COLORS = ANNOTATION_COLORS;
  protected readonly ANNOTATION_SIZE_FACTORS = ANNOTATION_SIZE_FACTORS;
  protected readonly textEditControl = new FormControl<string>('', { nonNullable: true });
  /** Nur damit Angular den Submit per Enter abfängt; ein `<form>` ohne Direktive würde die Seite neu laden. */
  protected readonly textEditForm = new FormGroup({ text: this.textEditControl });

  private stage: Konva.Stage | undefined;
  private content: Konva.Group | undefined;
  private shapesGroup: Konva.Group | undefined;
  private draftGroup: Konva.Group | undefined;
  private imageElement: HTMLImageElement | undefined;
  private imageWidth = 0;
  private imageHeight = 0;
  private fitScale = 1;
  private draft: AnnotationShape | null = null;
  private pinchLastDistance = 0;
  private pinchLastCenter: { x: number; y: number } | null = null;

  constructor() {
    afterNextRender(() => {
      // An den <body> hängen: In Ionic bildet der Scroll-Container von ion-content eine eigene Stapelebene (z-index 0)
      // unter dem ion-header — dort läge der Vollbild-Editor trotz hohem z-index unter der Kopfzeile.
      this.document.body.appendChild(this.hostElement.nativeElement);
      this.createStage();
      this.loadImage(this.imageSrc());
    });
    this.destroyRef.onDestroy(() => {
      this.stage?.destroy();
      this.hostElement.nativeElement.remove();
    });
  }

  protected selectTool(tool: AnnotationTool): void {
    this.tool.set(tool);
    if (tool !== 'select') {
      this.selectedIndex.set(null);
    }
  }

  protected selectColor(color: string): void {
    this.color.set(color);
    this.updateSelectedShape((shape) => ({ ...shape, color }));
  }

  protected selectSizeFactor(sizeFactor: number): void {
    this.sizeFactor.set(sizeFactor);
    this.updateSelectedShape((shape) => ({ ...shape, strokeWidth: this.getStrokeWidth() }));
  }

  protected undo(): void {
    const undoStack = this.undoStack();
    if (!undoStack.length) {
      return;
    }
    this.redoStack.update((redoStack) => [...redoStack, this.shapes()]);
    this.shapes.set(undoStack[undoStack.length - 1]);
    this.undoStack.set(undoStack.slice(0, -1));
    this.selectedIndex.set(null);
  }

  protected redo(): void {
    const redoStack = this.redoStack();
    if (!redoStack.length) {
      return;
    }
    this.undoStack.update((undoStack) => [...undoStack, this.shapes()]);
    this.shapes.set(redoStack[redoStack.length - 1]);
    this.redoStack.set(redoStack.slice(0, -1));
    this.selectedIndex.set(null);
  }

  protected deleteSelected(): void {
    const selectedIndex = this.selectedIndex();
    if (selectedIndex === null) {
      return;
    }
    this.commit(this.shapes().filter((_, index) => index !== selectedIndex));
    this.selectedIndex.set(null);
  }

  protected confirmTextEdit(): void {
    const textEdit = this.textEdit();
    if (!textEdit) {
      return;
    }
    const value = this.textEditControl.value.trim();
    this.textEdit.set(null);

    if (textEdit.index === null) {
      if (value) {
        this.commit([
          ...this.shapes(),
          { type: 'text', x: textEdit.x, y: textEdit.y, text: value, color: this.color(), strokeWidth: this.getStrokeWidth() },
        ]);
      }
      return;
    }

    const shape = this.shapes()[textEdit.index];
    if (shape?.type === 'text') {
      // Leerer Text = Element entfernen; ein unsichtbares Textfeld wäre nur noch per Zufall auffindbar.
      this.commit(
        value
          ? this.shapes().map((existing, index) => (index === textEdit.index ? { ...shape, text: value } : existing))
          : this.shapes().filter((_, index) => index !== textEdit.index),
      );
      this.selectedIndex.set(null);
    } else if (shape?.type === 'dimension') {
      this.commit(this.shapes().map((existing, index) => (index === textEdit.index ? { ...shape, label: value } : existing)));
    }
  }

  /** Schliessen ohne Speichern: bei Änderungen erst fragen, ob gespeichert oder verworfen werden soll. */
  protected requestClose(): void {
    if (!this.hasUnsavedChanges()) {
      this.cancelled.emit();
      return;
    }
    this.unsavedChangesModal().open();
  }

  protected async saveFromUnsavedChangesModal(): Promise<void> {
    await this.unsavedChangesModal().close();
    await this.save();
  }

  protected async discardChanges(): Promise<void> {
    await this.unsavedChangesModal().close();
    this.cancelled.emit();
  }

  protected async continueEditing(): Promise<void> {
    await this.unsavedChangesModal().close();
  }

  protected cancelTextEdit(): void {
    this.textEdit.set(null);
  }

  protected async save(): Promise<void> {
    if (!this.imageElement || this.isBusy()) {
      return;
    }
    this.isExporting.set(true);
    try {
      const blob = await this.exportImage(this.imageElement);
      this.saved.emit({ blob, annotationJson: serializeImageAnnotation(this.shapes(), this.imageWidth, this.imageHeight) });
    } finally {
      this.isExporting.set(false);
    }
  }

  protected handleKeydown(event: KeyboardEvent): void {
    // Die Rückfrage schliesst sich mit Escape selbst.
    if (this.unsavedChangesModal().isOpen) {
      return;
    }
    if (this.textEdit()) {
      if (event.key === 'Escape') {
        this.cancelTextEdit();
      }
      return;
    }
    const isModifierPressed = event.metaKey || event.ctrlKey;
    if (event.key === 'Escape') {
      this.requestClose();
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && this.selectedIndex() !== null) {
      event.preventDefault();
      this.deleteSelected();
    } else if (isModifierPressed && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      if (event.shiftKey) {
        this.redo();
      } else {
        this.undo();
      }
    }
  }

  protected handleResize(): void {
    if (!this.stage) {
      return;
    }
    const container = this.stageContainer().nativeElement;
    this.stage.size({ width: container.clientWidth, height: container.clientHeight });
    this.fitToScreen();
    this.renderShapes(this.shapes(), this.selectedIndex(), this.tool());
  }

  private createStage(): void {
    const container = this.stageContainer().nativeElement;
    this.stage = new Konva.Stage({ container, width: container.clientWidth, height: container.clientHeight });
    const layer = new Konva.Layer();
    this.content = new Konva.Group();
    this.shapesGroup = new Konva.Group();
    this.draftGroup = new Konva.Group({ listening: false });
    this.content.add(this.shapesGroup, this.draftGroup);
    layer.add(this.content);
    this.stage.add(layer);

    this.stage.on('mousedown touchstart', (event) => this.handlePointerDown(event));
    this.stage.on('mousemove touchmove', (event) => this.handlePointerMove(event));
    this.stage.on('mouseup touchend', (event) => this.handlePointerUp(event));
    this.stage.on('wheel', (event) => this.handleWheel(event as Konva.KonvaEventObject<WheelEvent>));
  }

  private loadImage(imageSrc: string): void {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      this.imageElement = image;
      this.imageWidth = image.naturalWidth;
      this.imageHeight = image.naturalHeight;
      this.content?.add(new Konva.Image({ image, width: this.imageWidth, height: this.imageHeight, listening: true, name: 'background' }));
      this.content?.findOne('.background')?.moveToBottom();
      this.shapes.set(parseImageAnnotation(this.annotationJson(), this.imageWidth));
      this.initialShapes.set(this.shapes());
      this.fitToScreen();
      this.isImageLoading.set(false);
      this.renderShapes(this.shapes(), this.selectedIndex(), this.tool());
    };
    image.onerror = () => {
      this.isImageLoading.set(false);
      this.imageLoadFailed.set(true);
    };
    // Eigener Query-Parameter: ein ohne CORS-Header gecachtes Bild aus der Lightbox würde den Canvas sonst sperren.
    image.src = `${imageSrc}${imageSrc.includes('?') ? '&' : '?'}annotate=1`;
  }

  private fitToScreen(): void {
    if (!this.stage || !this.content || !this.imageWidth) {
      return;
    }
    this.fitScale = Math.min(this.stage.width() / this.imageWidth, this.stage.height() / this.imageHeight);
    this.content.scale({ x: this.fitScale, y: this.fitScale });
    this.content.position({
      x: (this.stage.width() - this.imageWidth * this.fitScale) / 2,
      y: (this.stage.height() - this.imageHeight * this.fitScale) / 2,
    });
  }

  private renderShapes(shapes: AnnotationShape[], selectedIndex: number | null, tool: AnnotationTool): void {
    if (!this.shapesGroup || !this.content) {
      return;
    }
    const isSelectTool = tool === 'select';
    this.shapesGroup.destroyChildren();
    this.shapesGroup.listening(isSelectTool);
    this.content.draggable(isSelectTool);

    const minHitStrokeWidth = MIN_HIT_WIDTH_ON_SCREEN / this.content.scaleX();
    shapes.forEach((shape, index) => {
      const node = buildAnnotationNode(shape, minHitStrokeWidth);
      node.draggable(isSelectTool);
      node.on('click tap', (event) => {
        event.cancelBubble = true;
        this.selectedIndex.set(index);
        this.color.set(shape.color);
      });
      node.on('dblclick dbltap', (event) => {
        event.cancelBubble = true;
        this.openTextEditForShape(index);
      });
      node.on('dragend', (event) => {
        event.cancelBubble = true;
        const moved = translateAnnotationShape(shape, node.x(), node.y());
        this.commit(this.shapes().map((existing, existingIndex) => (existingIndex === index ? moved : existing)));
      });
      this.shapesGroup?.add(node);

      if (index === selectedIndex && isSelectTool && this.isSegmentShape(shape)) {
        this.addEndpointHandles(shape, index, node);
      } else if (index === selectedIndex) {
        const bounds = node.getClientRect({ relativeTo: this.shapesGroup });
        const padding = shape.strokeWidth * 2;
        this.shapesGroup?.add(
          new Konva.Rect({
            x: bounds.x - padding,
            y: bounds.y - padding,
            width: bounds.width + padding * 2,
            height: bounds.height + padding * 2,
            stroke: SELECTION_COLOR,
            strokeWidth: Math.max(1, shape.strokeWidth / 2),
            dash: [shape.strokeWidth * 2, shape.strokeWidth * 2],
            listening: false,
          }),
        );
      }
    });
    this.stage?.batchDraw();
  }

  private isSegmentShape(shape: AnnotationShape): shape is SegmentAnnotationShape {
    return shape.type === 'line' || shape.type === 'arrow' || shape.type === 'dimension';
  }

  /**
   * Zwei ziehbare Griffe an den Enden einer gewählten Linie, eines Pfeils oder einer Vermassung: so lässt sich ein Ende
   * nachjustieren, ohne die Form neu zu zeichnen. Während des Ziehens wird nur die Form selbst neu aufgebaut; erst beim
   * Loslassen landet die Änderung im Verlauf (Undo) und alles wird neu gerendert.
   */
  private addEndpointHandles(shape: SegmentAnnotationShape, index: number, initialNode: Konva.Group): void {
    if (!this.shapesGroup || !this.content) {
      return;
    }
    const scale = this.content.scaleX();
    const minHitStrokeWidth = MIN_HIT_WIDTH_ON_SCREEN / scale;
    let shapeNode = initialNode;
    let points = [...shape.points] as SegmentAnnotationShape['points'];

    [0, 1].forEach((endpointIndex) => {
      const handle = new Konva.Circle({
        x: points[endpointIndex * 2],
        y: points[endpointIndex * 2 + 1],
        radius: ENDPOINT_HANDLE_RADIUS_ON_SCREEN / scale,
        hitStrokeWidth: (ENDPOINT_HANDLE_HIT_RADIUS_ON_SCREEN * 2) / scale,
        fill: '#ffffff',
        stroke: SELECTION_COLOR,
        strokeWidth: 3 / scale,
        draggable: true,
        name: 'endpoint-handle',
      });
      handle.on('mousedown touchstart click tap', (event) => {
        event.cancelBubble = true;
      });
      handle.on('dragmove', (event) => {
        event.cancelBubble = true;
        points = [...points] as SegmentAnnotationShape['points'];
        points[endpointIndex * 2] = handle.x();
        points[endpointIndex * 2 + 1] = handle.y();
        const replacement = buildAnnotationNode({ ...shape, points }, minHitStrokeWidth);
        replacement.listening(false);
        shapeNode.destroy();
        this.shapesGroup?.add(replacement);
        replacement.zIndex(index);
        shapeNode = replacement;
        this.stage?.batchDraw();
      });
      handle.on('dragend', (event) => {
        event.cancelBubble = true;
        this.commit(this.shapes().map((existing, existingIndex) => (existingIndex === index ? { ...shape, points } : existing)));
      });
      this.shapesGroup?.add(handle);
    });
  }

  private handlePointerDown(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    if (this.isMultiTouch(event) || this.textEdit() || !this.content || !this.imageWidth) {
      return;
    }
    const tool = this.tool();
    if (tool === 'select') {
      // Klick ins Leere hebt die Auswahl auf; das Verschieben des Bildes übernimmt Konva über `draggable`.
      // Bei Klick auf eine Form nicht: das Neuzeichnen würde den Knoten zerstören, bevor sein click/drag ankommt.
      if (!this.shapesGroup?.isAncestorOf(event.target)) {
        this.selectedIndex.set(null);
      }
      return;
    }
    const position = this.content.getRelativePointerPosition();
    if (!position) {
      return;
    }
    const style = { color: this.color(), strokeWidth: this.getStrokeWidth() };
    switch (tool) {
      case 'freehand':
        this.draft = { type: 'freehand', points: [position.x, position.y], ...style };
        break;
      case 'line':
      case 'arrow':
      case 'dimension':
        this.draft = { type: tool, points: [position.x, position.y, position.x, position.y], ...style };
        break;
      case 'rect':
      case 'ellipse':
        this.draft = { type: tool, x: position.x, y: position.y, width: 0, height: 0, ...style };
        break;
      case 'text':
        this.openTextEdit({ index: null, mode: 'text', x: position.x, y: position.y }, '');
        break;
    }
  }

  private handlePointerMove(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    if (this.isMultiTouch(event)) {
      event.evt.preventDefault();
      this.draft = null;
      this.draftGroup?.destroyChildren();
      this.handlePinch(event.evt as TouchEvent);
      return;
    }
    if (!this.draft || !this.content) {
      return;
    }
    event.evt.preventDefault();
    const position = this.content.getRelativePointerPosition();
    if (!position) {
      return;
    }
    const draft = this.draft;
    switch (draft.type) {
      case 'freehand':
        this.draft = { ...draft, points: [...draft.points, position.x, position.y] };
        break;
      case 'line':
      case 'arrow':
      case 'dimension':
        this.draft = { ...draft, points: [draft.points[0], draft.points[1], position.x, position.y] };
        break;
      case 'rect':
      case 'ellipse':
        this.draft = { ...draft, width: position.x - draft.x, height: position.y - draft.y };
        break;
    }
    this.draftGroup?.destroyChildren();
    this.draftGroup?.add(buildAnnotationNode(this.draft));
    this.stage?.batchDraw();
  }

  private handlePointerUp(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    if ((event.evt as TouchEvent).touches?.length < 2) {
      this.pinchLastCenter = null;
      this.pinchLastDistance = 0;
    }
    const draft = this.draft;
    this.draft = null;
    this.draftGroup?.destroyChildren();
    this.stage?.batchDraw();
    if (!draft || !isAnnotationShapeMeaningful(draft)) {
      return;
    }
    const normalized: AnnotationShape =
      draft.type === 'rect' || draft.type === 'ellipse'
        ? {
            ...draft,
            x: Math.min(draft.x, draft.x + draft.width),
            y: Math.min(draft.y, draft.y + draft.height),
            width: Math.abs(draft.width),
            height: Math.abs(draft.height),
          }
        : draft;
    this.commit([...this.shapes(), normalized]);
    if (normalized.type === 'dimension') {
      this.openTextEditForShape(this.shapes().length - 1);
    }
  }

  private handleWheel(event: Konva.KonvaEventObject<WheelEvent>): void {
    event.evt.preventDefault();
    const pointer = this.stage?.getPointerPosition();
    if (!pointer) {
      return;
    }
    this.zoomAround(pointer, event.evt.deltaY < 0 ? 1.1 : 1 / 1.1, { x: 0, y: 0 });
  }

  private handlePinch(touchEvent: TouchEvent): void {
    if (!this.stage || !this.content) {
      return;
    }
    if (this.content.isDragging()) {
      this.content.stopDrag();
    }
    const containerBounds = this.stageContainer().nativeElement.getBoundingClientRect();
    const [firstTouch, secondTouch] = [touchEvent.touches[0], touchEvent.touches[1]];
    const first = { x: firstTouch.clientX - containerBounds.left, y: firstTouch.clientY - containerBounds.top };
    const second = { x: secondTouch.clientX - containerBounds.left, y: secondTouch.clientY - containerBounds.top };
    const center = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
    const distance = Math.hypot(second.x - first.x, second.y - first.y);

    if (!this.pinchLastCenter || !this.pinchLastDistance) {
      this.pinchLastCenter = center;
      this.pinchLastDistance = distance;
      return;
    }
    const panOffset = { x: center.x - this.pinchLastCenter.x, y: center.y - this.pinchLastCenter.y };
    this.zoomAround(center, distance / this.pinchLastDistance, panOffset);
    this.pinchLastCenter = center;
    this.pinchLastDistance = distance;
  }

  private zoomAround(point: { x: number; y: number }, factor: number, panOffset: { x: number; y: number }): void {
    if (!this.content) {
      return;
    }
    const oldScale = this.content.scaleX();
    const newScale = Math.min(Math.max(oldScale * factor, this.fitScale), this.fitScale * MAX_ZOOM_FACTOR);
    const pointInImage = { x: (point.x - this.content.x()) / oldScale, y: (point.y - this.content.y()) / oldScale };
    this.content.scale({ x: newScale, y: newScale });
    this.content.position({
      x: point.x - pointInImage.x * newScale + panOffset.x,
      y: point.y - pointInImage.y * newScale + panOffset.y,
    });
    // Neu aufbauen, damit die Trefferflächen zum neuen Zoom passen.
    this.renderShapes(this.shapes(), this.selectedIndex(), this.tool());
  }

  private openTextEditForShape(index: number): void {
    const shape = this.shapes()[index];
    if (shape?.type === 'text') {
      this.openTextEdit({ index, mode: 'text', x: shape.x, y: shape.y }, shape.text);
    } else if (shape?.type === 'dimension') {
      this.openTextEdit({ index, mode: 'label', x: shape.points[0], y: shape.points[1] }, shape.label ?? '');
    }
  }

  private openTextEdit(textEdit: TextEditState, value: string): void {
    this.textEditControl.setValue(value);
    this.textEdit.set(textEdit);
    setTimeout(() => this.textInputField()?.focus());
  }

  private updateSelectedShape(update: (shape: AnnotationShape) => AnnotationShape): void {
    const selectedIndex = this.selectedIndex();
    if (selectedIndex === null) {
      return;
    }
    this.commit(this.shapes().map((shape, index) => (index === selectedIndex ? update(shape) : shape)));
  }

  private commit(shapes: AnnotationShape[]): void {
    this.undoStack.update((undoStack) => [...undoStack, this.shapes()]);
    this.redoStack.set([]);
    this.shapes.set(shapes);
  }

  private getStrokeWidth(): number {
    return Math.round(getBaseStrokeWidth(this.imageWidth, this.imageHeight) * this.sizeFactor() * 10) / 10;
  }

  private isMultiTouch(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>): boolean {
    return ((event.evt as TouchEvent).touches?.length ?? 0) > 1;
  }

  /** Rendert Bild und Zeichnung in einer eigenen, unsichtbaren Stage in Originalgrösse — unabhängig vom Anzeige-Zoom. */
  private async exportImage(image: HTMLImageElement): Promise<Blob> {
    const exportStage = new Konva.Stage({ container: document.createElement('div'), width: this.imageWidth, height: this.imageHeight });
    try {
      const layer = new Konva.Layer();
      exportStage.add(layer);
      layer.add(new Konva.Image({ image, width: this.imageWidth, height: this.imageHeight }));
      for (const shape of this.shapes()) {
        layer.add(buildAnnotationNode(shape));
      }
      return (await exportStage.toBlob({ mimeType: 'image/jpeg', quality: 0.9, pixelRatio: 1 })) as Blob;
    } finally {
      exportStage.destroy();
    }
  }
}
